import { IS_LOGGING_OUT_KEY, LOGGING_OUT_VALUE } from 'common/constants/auth/session';
import {
  AUTH_ACCESS_TOKEN_STORAGE_KEY,
  AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY,
  AUTH_OTP_VERIFIED_LEGACY_STORAGE_KEY,
  AUTH_OTP_VERIFIED_STORAGE_KEY,
  AUTH_PENDING_BACKUP_CODES_STORAGE_KEY,
  AUTH_PENDING_BACKUP_CODES_TTL_MS,
  AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY,
  AUTH_PENDING_SIGN_IN_IDENTIFIER_TTL_MS,
  AUTH_REFRESH_TOKEN_STORAGE_KEY,
  AUTH_SIGN_IN_REMEMBER_STORAGE_KEY,
  AUTH_STORAGE_PERSISTENCE_LOCAL,
  AUTH_STORAGE_PERSISTENCE_SESSION,
  type AuthTokenPersistence,
} from 'common/constants/auth/storage';

const AUTH_SESSION_SYNC_CHANNEL_NAME = 'scalar-auth-session-sync';

// Session-scoped auth should remain tab-local. Cross-tab sync is only used
// to fan out logout events so sibling tabs do not keep stale authenticated UI.
type AuthSessionSyncMessage = { type: 'logout' };

let authSessionSyncChannel: BroadcastChannel | null | undefined;

// Read both names during the migration so existing persisted sessions keep their 2FA state
// until every active bundle writes and reads only the canonical key.
const AUTH_OTP_VERIFIED_STORAGE_KEYS = [AUTH_OTP_VERIFIED_STORAGE_KEY, AUTH_OTP_VERIFIED_LEGACY_STORAGE_KEY] as const;

const AUTH_TOKEN_STORAGE_KEYS = [AUTH_ACCESS_TOKEN_STORAGE_KEY, AUTH_REFRESH_TOKEN_STORAGE_KEY] as const;

interface PendingSignInIdentifierState {
  expiresAt: number;
  identifier: string;
}

interface PendingBackupCodesState {
  backupCodes: string[];
  expiresAt: number;
}

const migrateRememberSignInPreferenceFromSession = () => {
  const sessionValue = sessionStorage.getItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY);

  if (sessionValue !== null && localStorage.getItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY) === null) {
    localStorage.setItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY, sessionValue);
  }

  sessionStorage.removeItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY);
};

// Fail closed: only an explicit opt-in keeps the session remembered. An absent key means the user
// never made a choice, which must stay remembered so sessions predating the checkbox are not demoted
// out of localStorage.
const isRememberSignInOptIn = (value: string | null) => value === null || value === '1' || value === 'true';

export const getRememberSignInPreference = () => {
  migrateRememberSignInPreferenceFromSession();

  return isRememberSignInOptIn(localStorage.getItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY));
};

// Pure read — never mutate storage here
const getStoredToken = (key: string) => sessionStorage.getItem(key) ?? localStorage.getItem(key);

const clearStoredToken = (key: string) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

// Only a promotion to localStorage may drop the tab copy
const writeStoredToken = (key: string, value: string, persistence: AuthTokenPersistence) => {
  if (persistence === AUTH_STORAGE_PERSISTENCE_LOCAL) {
    sessionStorage.removeItem(key);
    localStorage.setItem(key, value);
    return;
  }

  sessionStorage.setItem(key, value);
};

export const isAuthLogoutInProgress = () => localStorage.getItem(IS_LOGGING_OUT_KEY) === LOGGING_OUT_VALUE;

const hasTrueStoredFlag = (storage: Storage, keys: readonly string[]) =>
  keys.some(key => storage.getItem(key) === 'true');

const setStoredFlag = ({ keys, storage, value }: { keys: readonly string[]; storage: Storage; value: boolean }) => {
  keys.forEach(key => {
    if (!value) {
      storage.removeItem(key);
      return;
    }

    // Keep the legacy auth flag in sync during rollout so existing tabs do not drift
    storage.setItem(key, 'true');
  });
};

export const getSignInTokenPersistence = (rememberSignIn = getRememberSignInPreference()): AuthTokenPersistence =>
  rememberSignIn ? AUTH_STORAGE_PERSISTENCE_LOCAL : AUTH_STORAGE_PERSISTENCE_SESSION;

// Follow the scope the live session already uses rather than the stay-signed-in preference
const getActiveTokenPersistence = (): AuthTokenPersistence => {
  if (sessionStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)) {
    return AUTH_STORAGE_PERSISTENCE_SESSION;
  }

  if (localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)) {
    return AUTH_STORAGE_PERSISTENCE_LOCAL;
  }

  // No session stored yet (first sign-in): honour what the user asked for.
  return getSignInTokenPersistence();
};

const getAuthSessionSyncChannel = () => {
  if (authSessionSyncChannel !== undefined) {
    return authSessionSyncChannel;
  }

  authSessionSyncChannel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_SESSION_SYNC_CHANNEL_NAME);

  return authSessionSyncChannel;
};

export const setEnterpriseSsoSignInSession = () => {
  sessionStorage.setItem(AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY, '1');
};

export const clearEnterpriseSsoSignInSession = () => {
  sessionStorage.removeItem(AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY);
};

export const isEnterpriseSsoSignInSession = () =>
  sessionStorage.getItem(AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY) === '1';

export const isOtpVerified = () => hasTrueStoredFlag(localStorage, AUTH_OTP_VERIFIED_STORAGE_KEYS);

export const setOtpVerified = () => {
  setStoredFlag({
    keys: AUTH_OTP_VERIFIED_STORAGE_KEYS,
    storage: localStorage,
    value: true,
  });
};

export const clearOtpVerified = () => {
  setStoredFlag({
    keys: AUTH_OTP_VERIFIED_STORAGE_KEYS,
    storage: localStorage,
    value: false,
  });
};

export const getStoredAccessToken = () => getStoredToken(AUTH_ACCESS_TOKEN_STORAGE_KEY);

const getUserIdFromAccessToken = (accessToken: string | null): number | null => {
  if (!accessToken) {
    return null;
  }

  try {
    const [, rawPayload] = accessToken.split('.');
    if (!rawPayload) {
      return null;
    }

    const normalizedPayload = rawPayload.replaceAll('-', '+').replaceAll('_', '/');
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
    const decodedPayload = JSON.parse(globalThis.atob(paddedPayload)) as { user_id?: number | string };
    const userId = Number(decodedPayload.user_id);

    return Number.isFinite(userId) ? userId : null;
  } catch {
    return null;
  }
};

export const getStoredAccessTokenUserId = (): number | null => getUserIdFromAccessToken(getStoredAccessToken());

export const setStoredAccessToken = (
  accessToken: string,
  persistence: AuthTokenPersistence = getActiveTokenPersistence()
) => {
  writeStoredToken(AUTH_ACCESS_TOKEN_STORAGE_KEY, accessToken, persistence);
};

export const clearStoredAccessToken = () => {
  clearStoredToken(AUTH_ACCESS_TOKEN_STORAGE_KEY);
};

export const getStoredRefreshToken = () => getStoredToken(AUTH_REFRESH_TOKEN_STORAGE_KEY);

export const setStoredRefreshToken = (
  refreshToken: string,
  persistence: AuthTokenPersistence = getActiveTokenPersistence()
) => {
  writeStoredToken(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken, persistence);
};

export const clearStoredRefreshToken = () => {
  clearStoredToken(AUTH_REFRESH_TOKEN_STORAGE_KEY);
};

export const setStoredAuthTokens = ({
  accessToken,
  refreshToken,
  persistence = getSignInTokenPersistence(),
}: {
  accessToken: string;
  refreshToken: string;
  persistence?: AuthTokenPersistence;
}) => {
  setStoredAccessToken(accessToken, persistence);
  setStoredRefreshToken(refreshToken, persistence);
};

// A refresh continues an existing session, so both tokens stay in the scope that session already uses.
export const setRefreshedAuthTokens = ({
  accessToken,
  refreshToken,
}: {
  accessToken: string;
  refreshToken?: string | null;
}) => {
  const persistence = getActiveTokenPersistence();

  setStoredAccessToken(accessToken, persistence);

  if (refreshToken) {
    setStoredRefreshToken(refreshToken, persistence);
  }
};

export const clearStoredAuthTokens = () => {
  clearStoredAccessToken();
  clearStoredRefreshToken();
};

/** Align stored JWT scope with the remember-sign-in preference during app init and checkbox changes. */
export const syncAuthTokensWithRememberPreference = (rememberSignIn = getRememberSignInPreference()) => {
  AUTH_TOKEN_STORAGE_KEYS.forEach(key => {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (rememberSignIn) {
      if (sessionValue && !localValue) {
        localStorage.setItem(key, sessionValue);
      }

      sessionStorage.removeItem(key);
      return;
    }

    if (localValue) {
      if (!sessionValue) {
        sessionStorage.setItem(key, localValue);
      }

      localStorage.removeItem(key);
    }
  });
};

export const setRememberSignInPreference = (value: boolean) => {
  migrateRememberSignInPreferenceFromSession();
  localStorage.setItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY, value ? '1' : '0');
  syncAuthTokensWithRememberPreference(value);
};

export const clearPendingSignInIdentifier = () => {
  sessionStorage.removeItem(AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY);
};

export const setPendingSignInIdentifier = (identifier: string, ttlMs = AUTH_PENDING_SIGN_IN_IDENTIFIER_TTL_MS) => {
  const normalizedIdentifier = identifier.trim();

  if (!normalizedIdentifier) {
    clearPendingSignInIdentifier();
    return;
  }

  sessionStorage.setItem(
    AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY,
    JSON.stringify({
      expiresAt: Date.now() + ttlMs,
      identifier: normalizedIdentifier,
    } satisfies PendingSignInIdentifierState)
  );
};

export const getPendingSignInIdentifier = () => {
  const rawValue = sessionStorage.getItem(AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<PendingSignInIdentifierState>;

    if (typeof parsedValue.identifier !== 'string' || typeof parsedValue.expiresAt !== 'number') {
      clearPendingSignInIdentifier();
      return null;
    }

    if (parsedValue.expiresAt <= Date.now()) {
      clearPendingSignInIdentifier();
      return null;
    }

    return parsedValue.identifier;
  } catch {
    clearPendingSignInIdentifier();
    return null;
  }
};

export const clearPendingBackupCodes = () => {
  sessionStorage.removeItem(AUTH_PENDING_BACKUP_CODES_STORAGE_KEY);
};

export const setPendingBackupCodes = (backupCodes: string[], ttlMs = AUTH_PENDING_BACKUP_CODES_TTL_MS) => {
  const normalizedBackupCodes = backupCodes.map(code => code.trim()).filter(Boolean);

  if (normalizedBackupCodes.length === 0) {
    clearPendingBackupCodes();
    return;
  }

  sessionStorage.setItem(
    AUTH_PENDING_BACKUP_CODES_STORAGE_KEY,
    JSON.stringify({
      backupCodes: normalizedBackupCodes,
      expiresAt: Date.now() + ttlMs,
    } satisfies PendingBackupCodesState)
  );
};

export const getPendingBackupCodes = () => {
  const rawValue = sessionStorage.getItem(AUTH_PENDING_BACKUP_CODES_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<PendingBackupCodesState>;

    if (
      !Array.isArray(parsedValue.backupCodes) ||
      parsedValue.backupCodes.some(code => typeof code !== 'string' || code.trim().length === 0) ||
      typeof parsedValue.expiresAt !== 'number'
    ) {
      clearPendingBackupCodes();
      return null;
    }

    if (parsedValue.expiresAt <= Date.now()) {
      clearPendingBackupCodes();
      return null;
    }

    return parsedValue.backupCodes;
  } catch {
    clearPendingBackupCodes();
    return null;
  }
};

export const hasStoredAccessToken = () => Boolean(getStoredAccessToken());

export const subscribeToAuthSessionSync = ({
  onRemoteLogout,
}: {
  onRemoteLogout?: () => void;
} = {}) => {
  const channel = getAuthSessionSyncChannel();
  if (!channel) {
    return () => undefined;
  }

  const handleMessage = (event: MessageEvent<AuthSessionSyncMessage>) => {
    const message = event.data;

    if (message.type === 'logout') {
      clearStoredAuthTokens();
      clearOtpVerified();
      clearEnterpriseSsoSignInSession();
      onRemoteLogout?.();
    }
  };

  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
  };
};

export const broadcastAuthLogout = () => {
  getAuthSessionSyncChannel()?.postMessage({ type: 'logout' } satisfies AuthSessionSyncMessage);
};
